import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { 
  Smile, Frown, Meh, Heart, Zap, Moon, Brain, 
  TrendingUp, Calendar, CheckCircle, RefreshCw,
  Sun, CloudRain, Angry, AlertCircle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Emotions = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  
  const [selectedMood, setSelectedMood] = useState(null);
  const [energyLevel, setEnergyLevel] = useState([5]);
  const [stressLevel, setStressLevel] = useState([5]);
  const [sleepQuality, setSleepQuality] = useState([5]);
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [todayLogged, setTodayLogged] = useState(false);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);

  const moods = [
    { id: 'happy', icon: Smile, label: 'Happy', color: 'text-yellow-500 bg-yellow-50 border-yellow-200', emoji: '😊' },
    { id: 'good', icon: Sun, label: 'Good', color: 'text-green-500 bg-green-50 border-green-200', emoji: '🙂' },
    { id: 'neutral', icon: Meh, label: 'Neutral', color: 'text-gray-500 bg-gray-50 border-gray-200', emoji: '😐' },
    { id: 'sad', icon: Frown, label: 'Sad', color: 'text-blue-500 bg-blue-50 border-blue-200', emoji: '😢' },
    { id: 'stressed', icon: AlertCircle, label: 'Stressed', color: 'text-orange-500 bg-orange-50 border-orange-200', emoji: '😰' },
    { id: 'anxious', icon: CloudRain, label: 'Anxious', color: 'text-purple-500 bg-purple-50 border-purple-200', emoji: '😟' },
  ];

  const tags = [
    'Work', 'Exercise', 'Family', 'Sleep', 'Diet', 'Medication', 
    'Social', 'Weather', 'Health', 'Travel', 'Relaxation', 'Hobby'
  ];

  useEffect(() => {
    fetchTodayEmotion();
    fetchHistory();
    fetchSummary();
  }, []);

  const fetchTodayEmotion = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emotions/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_logged_today) {
        setTodayLogged(true);
        const emotion = response.data.emotion;
        setSelectedMood(emotion.mood);
        setEnergyLevel([emotion.energy_level]);
        setStressLevel([emotion.stress_level]);
        setSleepQuality([emotion.sleep_quality]);
        setNotes(emotion.notes || '');
        setSelectedTags(emotion.tags || []);
      }
    } catch (error) {
      console.error('Error fetching today emotion:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emotions?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.emotions || []);
    } catch (error) {
      console.error('Error fetching emotion history:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emotions/summary?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMood) {
      toast({
        title: "Select a mood",
        description: "Please select how you're feeling today",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/emotions`, {
        mood: selectedMood,
        energy_level: energyLevel[0],
        stress_level: stressLevel[0],
        sleep_quality: sleepQuality[0],
        notes,
        tags: selectedTags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Mood logged! 🎉",
        description: "Your daily emotion check-in has been saved.",
      });
      
      setTodayLogged(true);
      fetchHistory();
      fetchSummary();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log emotion. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const getMoodEmoji = (mood) => {
    return moods.find(m => m.id === mood)?.emoji || '😐';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Heart className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-3xl font-bold">Daily Emotions</h2>
            <p className="text-pink-100 text-lg">Track your mood and well-being</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Check-in Card */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                How are you feeling today?
              </CardTitle>
              <CardDescription>
                {todayLogged ? "You've already logged today. You can update it." : "Take a moment to check in with yourself."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mood Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                  Select your mood
                </label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {moods.map((mood) => (
                    <button
                      key={mood.id}
                      onClick={() => setSelectedMood(mood.id)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedMood === mood.id
                          ? `${mood.color} border-current scale-105 shadow-md`
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <mood.icon className={`w-8 h-8 mx-auto mb-1 ${selectedMood === mood.id ? '' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium block">{mood.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Energy Level */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    Energy Level
                  </label>
                  <span className="text-sm font-bold text-yellow-600">{energyLevel[0]}/10</span>
                </div>
                <Slider
                  value={energyLevel}
                  onValueChange={setEnergyLevel}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Exhausted</span>
                  <span>Energized</span>
                </div>
              </div>

              {/* Stress Level */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                    Stress Level
                  </label>
                  <span className="text-sm font-bold text-orange-600">{stressLevel[0]}/10</span>
                </div>
                <Slider
                  value={stressLevel}
                  onValueChange={setStressLevel}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Calm</span>
                  <span>Very Stressed</span>
                </div>
              </div>

              {/* Sleep Quality */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Moon className="w-4 h-4 text-indigo-500" />
                    Sleep Quality (Last Night)
                  </label>
                  <span className="text-sm font-bold text-indigo-600">{sleepQuality[0]}/10</span>
                </div>
                <Slider
                  value={sleepQuality}
                  onValueChange={setSleepQuality}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  What's influencing your mood? (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-all ${
                        selectedTags.includes(tag) ? 'bg-purple-600' : 'hover:bg-purple-50'
                      }`}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Additional notes (optional)
                </label>
                <Textarea
                  placeholder="Any thoughts you'd like to record..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading || !selectedMood}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : todayLogged ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Update Today's Check-in
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-2" />
                    Save Check-in
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary Card */}
          {summary && (
            <Card className="shadow-lg border-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  30-Day Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="text-4xl">{getMoodEmoji(summary.most_common_mood)}</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Most common mood</p>
                  <p className="font-semibold capitalize">{summary.most_common_mood}</p>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                    <Zap className="w-5 h-5 mx-auto text-yellow-500 mb-1" />
                    <p className="text-lg font-bold">{summary.avg_energy_level}</p>
                    <p className="text-xs text-gray-500">Energy</p>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-lg font-bold">{summary.avg_stress_level}</p>
                    <p className="text-xs text-gray-500">Stress</p>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                    <Moon className="w-5 h-5 mx-auto text-indigo-500 mb-1" />
                    <p className="text-lg font-bold">{summary.avg_sleep_quality}</p>
                    <p className="text-xs text-gray-500">Sleep</p>
                  </div>
                </div>
                
                <p className="text-xs text-center text-gray-500">
                  Based on {summary.total_entries} check-ins
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent History */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Recent Check-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.slice(0, 5).map((entry) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                        <div>
                          <p className="font-medium capitalize text-sm">{entry.mood}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(entry.logged_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <p>⚡ {entry.energy_level}/10</p>
                        <p>😴 {entry.sleep_quality}/10</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No check-ins yet. Start tracking!</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Emotions;
